// @Vendors
import { cookies, sessionUtils } from '@openbank/cf-ui-architecture-core';
// Constants
import { ONBOARDING_INIT_LOCAL_STORAGE } from 'constants/app.constants';
import { REFRESH_PROPS } from '../components/footer/handlers/constants/auth-watcher-footer.handlers.constants';
// Enumerations
import { AuthScenariosEnumeration } from '../enumerations/auth-scenarios.enumeration';
import { StorageCookiesEnum, StorageLocalEnum, TimerEnum } from '@openbank/ziniaweb-ui-core-models';
// Resolvers
import { expireCurrentOrderResolver } from 'resolvers/expire-order.resolver';
// Types
import {
  AuthWatcherHandlersBuilderType,
  AuthWatcherHandlersReturnType,
} from 'providers/auth-watcher/handlers/types/auth-watcher.handlers.type';
// Utilities
import {
  isAboutToAccomplishOneMinuteToExpireSession,
  isOnboardingTimeout,
  isSuccessContainerVisible,
} from './utils/auth-watcher.handlers.utils';
import { navigateToCallbackUrl } from 'utils/app-callback-url.utils';

let ABOUT_EXPIRING_INTERVAL, ACCESS_INTERVAL, REFRESH_INTERVAL;

const clearAllSessionIntervalsProcesses = (): void => {
  clearInterval(ABOUT_EXPIRING_INTERVAL);
  clearInterval(ACCESS_INTERVAL);
  clearInterval(REFRESH_INTERVAL);
};

const cleanAllSessionTimersWhenSuccessEventHandler = ({
  currentState,
}: Pick<AuthWatcherHandlersBuilderType, 'currentState'>): void => {
  isSuccessContainerVisible(currentState) && clearAllSessionIntervalsProcesses();
};

const checkAccessTokenValidityEventHandler = ({
  setAuthConflict,
}: Pick<AuthWatcherHandlersBuilderType, 'setAuthConflict'>): void => {
  ACCESS_INTERVAL = setInterval(() => {
    const accessToken = cookies.getCookie(StorageCookiesEnum.ACCESS_TOKEN);

    if (!accessToken) {
      clearAllSessionIntervalsProcesses();
      setAuthConflict(AuthScenariosEnumeration.ACCESS);
    }
  }, TimerEnum.TIMEOUT_ONE_SECOND);
};

const createOneLastMinuteToGoTimeout = ({
  setAuthConflict,
  ...props
}: Omit<AuthWatcherHandlersBuilderType, 'currentState'>): void => {
  setAuthConflict(AuthScenariosEnumeration.REDIRECTING);

  setTimeout(async () => {
    await expireCurrentOrderResolver();
    navigateToCallbackUrl(props);
  }, TimerEnum.TIMEOUT_FIFTEEN_SECONDS);
};

const expireCurrentOrderOnTimeout = async (): Promise<void> => {
  await expireCurrentOrderResolver();
  localStorage.removeItem(ONBOARDING_INIT_LOCAL_STORAGE);
};

const tryToAutomaticallyRefreshSession = async (
  props: Omit<AuthWatcherHandlersBuilderType, 'currentState'>
): Promise<void> => {
  clearAllSessionIntervalsProcesses();

  const refreshed = await sessionUtils.refreshToken(REFRESH_PROPS);
  const onboardingTimeout = isOnboardingTimeout();

  if (refreshed && !onboardingTimeout) {
    setupAllSessionTimersOnDemandHandler(props);
  } else {
    onboardingTimeout ? expireCurrentOrderOnTimeout() : createOneLastMinuteToGoTimeout(props);
  }
};

const checkAuthorizationSessionExpirationEventHandler = ({
  setAuthConflict,
  ...props
}: Omit<AuthWatcherHandlersBuilderType, 'currentState'>): void => {
  ABOUT_EXPIRING_INTERVAL = setInterval(async () => {
    const sessionExpiresAt = cookies.getCookie(StorageLocalEnum.SESSION_EXPIRES_AT);

    if (
      !sessionExpiresAt ||
      isAboutToAccomplishOneMinuteToExpireSession(sessionExpiresAt) ||
      isOnboardingTimeout()
    ) {
      await tryToAutomaticallyRefreshSession({ ...props, setAuthConflict });
    }
  }, TimerEnum.TIMEOUT_ONE_SECOND);
};

const checkRefreshTokenValidityEventHandler = ({
  setAuthConflict,
}: Pick<AuthWatcherHandlersBuilderType, 'setAuthConflict'>): void => {
  REFRESH_INTERVAL = setInterval(() => {
    const refreshToken = cookies.getCookie(StorageCookiesEnum.REFRESH_TOKEN);

    if (!refreshToken) {
      clearAllSessionIntervalsProcesses();
      setAuthConflict(AuthScenariosEnumeration.REFRESH);
    }
  }, TimerEnum.TIMEOUT_ONE_SECOND);
};

const setupAllSessionTimersOnDemandHandler = ({
  setAuthConflict,
  ...props
}: Omit<AuthWatcherHandlersBuilderType, 'currentState'>): void => {
  checkAccessTokenValidityEventHandler({ setAuthConflict });
  checkAuthorizationSessionExpirationEventHandler({ ...props, setAuthConflict });
  checkRefreshTokenValidityEventHandler({ setAuthConflict });
};

const AuthWatcherHandlers = ({
  currentState,
  setAuthConflict,
  ...props
}: AuthWatcherHandlersBuilderType): AuthWatcherHandlersReturnType => ({
  handleSetupAllSessionTimersOnDemand: () =>
    setupAllSessionTimersOnDemandHandler({ ...props, setAuthConflict }),
  handleCleanAllSessionTimersWhenSuccessEvent: () =>
    cleanAllSessionTimersWhenSuccessEventHandler({ currentState }),
  handleClearAllSessionIntervalsProcesses: () => clearAllSessionIntervalsProcesses(),
});

export { AuthWatcherHandlers };
